import React, { useMemo } from 'react';

import { Box, ButtonBase, Chip, Typography } from '@material-ui/core';
import clsx from 'clsx';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';

import type { DpiaHistoryListItem } from 'config/api/dpiaRequests/dpiaRequests.types';
import processing_activities_messages from 'messages/processing_activities_messages';

import useStyles from '../ProcessingActivitiesAssetReviewsPage/ProcessingActivitiesAssetReviewsPage.styles';

import { getDpiaDecisionLabel } from './apaDpiaStatus.helpers';

const historyTimestamp = (item: DpiaHistoryListItem): number => {
  const timestamp = Date.parse(item.approvedAt || item.createdAt);
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

export const sortDpiaHistoryItems = (items: DpiaHistoryListItem[]): DpiaHistoryListItem[] =>
  [...items].sort((left, right) => historyTimestamp(right) - historyTimestamp(left));

const formatHistoryDate = (value: string | null): string => {
  if (!value) {
    return '';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return format(date, 'yyyy-MM-dd HH:mm');
};

type DpiaStatusMessageKey = keyof typeof processing_activities_messages.asset_ropa.owner_dpia.statuses;

type Props = {
  canOpenAssessments?: boolean;
  items: DpiaHistoryListItem[];
  onOpenItem?: (item: DpiaHistoryListItem) => void;
};

const DpiaHistoryList: React.FC<Props> = ({ items, onOpenItem, canOpenAssessments = false }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const messages = processing_activities_messages.asset_ropa.dpia_history;
  const statusMessages = processing_activities_messages.asset_ropa.owner_dpia.statuses;
  const sorted = useMemo(() => sortDpiaHistoryItems(items), [items]);

  if (sorted.length === 0) {
    return (
      <Typography color='textSecondary' variant='body2'>
        {t(messages.empty)}
      </Typography>
    );
  }

  return (
    <>
      {sorted.map(item => {
        const statusKey = item.status as DpiaStatusMessageKey;
        const statusLabel = statusMessages[statusKey] ? t(statusMessages[statusKey]) : item.status.replace(/_/g, ' ');
        const decisionLabel = item.decision ? getDpiaDecisionLabel(item.decision, t) : null;
        const date = formatHistoryDate(item.approvedAt || item.createdAt);
        const typeLabel = t(messages.types[item.kind]);
        const title =
          item.title ||
          (item.kind === 'dpia_indication' ? typeLabel : null) ||
          (item.kind === 'dpia_request'
            ? t(processing_activities_messages.asset_ropa.dpia_need_assessment.page_title)
            : t(messages.untitled));

        return (
          <Box key={`${item.kind}-${item.id}`} style={{ marginTop: 8 }}>
            <Chip label={typeLabel} size='small' style={{ marginBottom: 4 }} variant='outlined' />
            {onOpenItem && (item.kind === 'dpia' || (item.kind === 'dpia_request' && canOpenAssessments)) ? (
              <ButtonBase
                onClick={() => onOpenItem(item)}
                style={{ display: 'block', margin: 0, padding: 0, textAlign: 'left', textDecoration: 'underline' }}
                type='button'
              >
                <Typography color='primary' component='span' variant='body2'>
                  <strong>{title}</strong>
                </Typography>
              </ButtonBase>
            ) : (
              <Typography variant='body2'>
                <strong>{title}</strong>
              </Typography>
            )}
            <Typography color='textSecondary' variant='body2'>
              {statusLabel}
              {decisionLabel ? ` · ${decisionLabel}` : ''}
              {date ? ` · ${date}` : ''}
              {item.isCurrent && (
                <span className={clsx(styles.statusBadge, styles.statusBadgeSuccess)} style={{ marginLeft: 8 }}>
                  {t(messages.current)}
                </span>
              )}
            </Typography>
          </Box>
        );
      })}
    </>
  );
};

export default DpiaHistoryList;
